import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FaArrowLeft, FaBook, FaSchool, FaUsers, FaUserGraduate,
  FaCheckCircle, FaTimesCircle, FaChartBar, FaTrophy,
  FaClock, FaCalendarAlt, FaLanguage, FaStar,
  FaThumbsUp, FaThumbsDown, FaChartLine, FaPlayCircle, FaQuestionCircle
} from "react-icons/fa";
import { MdBarChart } from "react-icons/md";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from "recharts";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { formateDatemdy } from "../../utils/Helper";

const NAVY   = "#000666";
const TEAL   = "#006a60";
const CORAL  = "#f96b3f";
const PASS_C = "#006a60";
const FAIL_C = "#ba1a1a";
const ENROLL_COLORS = ["#4c56af", "#f96b3f"];

const CoursePerformance = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout, data } = useContext(UserContext);
  const user_type = data?.user_type;

  const [loading, setLoading]       = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [error, setError]           = useState(null);
  const [activeAccordion, setActiveAccordion] = useState(null);

  const headers = { "auth-token": authToken, "authorization": accessToken };

  useEffect(() => {
    if (courseId) fetchCoursePerformance();
  }, [courseId]); // eslint-disable-line

  const fetchCoursePerformance = async () => {
    setLoading(true); setError(null);
    try {
      const response = await APIServices.get(`/admin/courses/performance/${courseId}`, headers);
      if (response?.data?.status === true) {
        setCourseData(response?.data?.data);
      } else if (response?.status === 401) {
        logout(); navigate("/");
      } else {
        setError(response?.data?.message || "Failed to fetch course performance data");
      }
    } catch (err) {
      setError(err?.message || "Error fetching course performance data");
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceLabel = (passRate) => {
    if (passRate >= 70) return { text: "Excellent",         color: TEAL };
    if (passRate >= 50) return { text: "Average",           color: "#d97706" };
    return               { text: "Needs Improvement", color: FAIL_C };
  };

  /* ─── Loading ─── */
  if (loading) return (
    <div className="perf-loading">
      <div className="perf-spinner" />
      <p style={{ fontSize: 14, color: "var(--outline)" }}>Loading course performance…</p>
    </div>
  );

  /* ─── Error ─── */
  if (error) return (
    <div className="perf-loading">
      <FaTimesCircle size={40} style={{ color: FAIL_C }} />
      <p style={{ color: FAIL_C, fontWeight: 600 }}>{error}</p>
      <button className="red_button" onClick={() => navigate(-1)}>
        <FaArrowLeft className="me-1" /> Go Back
      </button>
    </div>
  );

  if (!courseData) return (
    <div className="perf-loading">
      <p style={{ color: "var(--outline)" }}>No data available</p>
      <button className="red_button" onClick={() => navigate(-1)}>
        <FaArrowLeft className="me-1" /> Go Back
      </button>
    </div>
  );

  const { course, enrollment_stats, overall_performance, topics } = courseData;

  const passFailData = [
    { name: "Passed", value: overall_performance?.total_passes || 0, color: PASS_C },
    { name: "Failed", value: overall_performance?.total_fails  || 0, color: FAIL_C },
  ];
  const enrollmentData = [
    { name: "Started",     value: enrollment_stats?.total_students_started   || 0, color: ENROLL_COLORS[0] },
    { name: "Not Started", value: enrollment_stats?.not_started_students     || 0, color: ENROLL_COLORS[1] },
  ];

  const passMax = Math.max(...passFailData.map(d => d.value), 0);
  const enrMax  = Math.max(...enrollmentData.map(d => d.value), 0);
  const smartY  = (max) => max <= 5
    ? { allowDecimals: false, domain: [0, 5], ticks: [0,1,2,3,4,5] }
    : { allowDecimals: false };

  return (
    <div className="perf-page">

      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="font_blue_20 font-600 mb-0">Course Performance</h1>
        <button className="blue_border d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
          <FaArrowLeft size={13} /> Back
        </button>
      </div>

      {/* ── Course Info Card ── */}
      <div className="perf-card mb-4">
        <div className="perf-card-header navy">
          <FaBook /> {course?.title || "Course Details"}
        </div>
        <div className="perf-card-body">
          <div className="d-flex gap-4 flex-wrap">
            {course?.image ? (
              <img src={course.image} alt={course.title}
                style={{ width: 160, height: 120, objectFit: "cover", borderRadius: "var(--radius-md)", flexShrink: 0 }} />
            ) : (
              <div style={{ width: 160, height: 120, background: "var(--surface-low)", borderRadius: "var(--radius-md)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaBook size={40} style={{ color: "var(--outline)" }} />
              </div>
            )}
            <div className="flex-grow-1">
              <h2 className="font_blue_18 font-600 mb-1">{course?.title || "N/A"}</h2>
              <p className="font_grey_12 mb-3">{course?.sub_title || ""}</p>
              <div className="perf-info-grid" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                <div className="perf-info-item">
                  <div className="perf-info-icon navy"><FaBook /></div>
                  <div><p className="perf-info-label">Course ID</p><p className="perf-info-value">{course?.unique_course_id || "N/A"}</p></div>
                </div>
                <div className="perf-info-item">
                  <div className="perf-info-icon teal"><FaClock /></div>
                  <div><p className="perf-info-label">Duration</p><p className="perf-info-value">{course?.duration || "N/A"}</p></div>
                </div>
                <div className="perf-info-item">
                  <div className="perf-info-icon coral"><FaLanguage /></div>
                  <div><p className="perf-info-label">Language</p><p className="perf-info-value">{course?.language || "N/A"}</p></div>
                </div>
                <div className="perf-info-item">
                  <div className="perf-info-icon amber"><FaCalendarAlt /></div>
                  <div><p className="perf-info-label">Expiry Date</p><p className="perf-info-value">{course?.expiry_date ? formateDatemdy(course.expiry_date) : "N/A"}</p></div>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="perf-badge info"><FaUserGraduate /> Grades: {course?.grade_names?.length || 0}</span>
                <span className={`perf-badge ${course?.published_status === "Published" ? "success" : "neutral"}`}>
                  {course?.published_status || "N/A"}
                </span>
                <span className="perf-badge neutral">
                  Created: {course?.created_at ? formateDatemdy(course.created_at) : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI Grid (admin only) ── */}
      {user_type !== "school" && (
        <div className="perf-kpi-grid">
          <div className="perf-kpi-card">
            <div className="perf-kpi-icon navy"><FaSchool /></div>
            <div>
              <p className="perf-kpi-label">Schools Mapped</p>
              <h3 className="perf-kpi-value">{enrollment_stats?.total_schools_mapped || 0}</h3>
            </div>
          </div>
          <div className="perf-kpi-card">
            <div className="perf-kpi-icon teal"><FaUsers /></div>
            <div>
              <p className="perf-kpi-label">Students Assigned</p>
              <h3 className="perf-kpi-value">{enrollment_stats?.total_students_assigned || 0}</h3>
            </div>
          </div>
          <div className="perf-kpi-card">
            <div className="perf-kpi-icon coral"><FaPlayCircle /></div>
            <div>
              <p className="perf-kpi-label">Started Course</p>
              <h3 className="perf-kpi-value">{enrollment_stats?.total_students_started || 0}</h3>
            </div>
          </div>
          <div className="perf-kpi-card">
            <div className="perf-kpi-icon amber"><FaTrophy /></div>
            <div>
              <p className="perf-kpi-label">Completion Rate</p>
              <h3 className="perf-kpi-value">{enrollment_stats?.completion_rate || 0}%</h3>
              <div className="perf-progress" style={{ width: 80 }}>
                <div className="perf-progress-fill" style={{ width: `${enrollment_stats?.completion_rate || 0}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Charts ── */}
      <div className="perf-charts-grid">
        {/* Pass vs Fail */}
        <div className="perf-card">
          <div className="perf-card-header navy">
            <FaChartBar /> Pass vs Fail Distribution
          </div>
          <div className="perf-card-body">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={passFailData} cx="50%" cy="50%" outerRadius={90}
                  labelLine={false}
                  label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : null}
                  dataKey="value">
                  {passFailData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <p style={{ textAlign: "center", fontSize: 12, color: "var(--outline)", marginTop: 8 }}>
              Total Attempts: {overall_performance?.total_lesson_attempts || 0}
            </p>
          </div>
        </div>

        {/* Enrollment Status */}
        <div className="perf-card">
          <div className="perf-card-header teal">
            <FaUserGraduate /> Student Enrollment Status
          </div>
          <div className="perf-card-body">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={enrollmentData} cx="50%" cy="50%" outerRadius={90}
                  labelLine={false}
                  label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : null}
                  dataKey="value">
                  {enrollmentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <p style={{ textAlign: "center", fontSize: 12, color: "var(--outline)", marginTop: 8 }}>
              Total Students: {enrollment_stats?.total_students_assigned || 0}
            </p>
          </div>
        </div>
      </div>

      {/* ── Topic-wise Lesson Performance ── */}
      <p className="perf-section-title"><FaBook /> Topic-wise Lesson Performance</p>
      <Accordion activeKey={activeAccordion} onSelect={setActiveAccordion} className="perf-accordion">
        {topics?.map((topic, i) => (
          <Accordion.Item eventKey={String(i)} key={topic.id}>
            <Accordion.Header>
              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                <div>
                  <strong>{topic.sr_no} — {topic.name}</strong>
                  <div style={{ marginTop: 4 }}>
                    <span className="perf-badge neutral" style={{ fontSize: 11 }}>{topic.no_of_lesson} Lessons</span>
                  </div>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <div className="todoList_table">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Lesson Name</th>
                      <th className="text-center">Questions</th>
                      <th className="text-center">Attempts</th>
                      <th className="text-center">Pass / Fail</th>
                      <th className="text-center">1st <FaStar style={{ color: "#d97706" }} size={11} /></th>
                      <th className="text-center">2nd</th>
                      <th className="text-center">3rd</th>
                      <th className="text-center">4th+</th>
                      <th className="text-center">Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topic.lessons?.map((lesson) => {
                      const perf = lesson.performance || {};
                      const { text, color } = getPerformanceLabel(perf.pass_rate || 0);
                      return (
                        <tr key={lesson.id}>
                          <td>{lesson.sr_no}</td>
                          <td><strong>{lesson.name}</strong></td>
                          <td className="text-center">
                            <span className="perf-badge info"><FaQuestionCircle size={10} /> {lesson.no_of_question || 0}</span>
                          </td>
                          <td className="text-center">
                            <span className="perf-badge neutral">{perf.total_attempts || 0}</span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <OverlayTrigger overlay={<Tooltip>Passed: {perf.pass_count || 0}</Tooltip>}>
                                <span className="perf-badge success"><FaCheckCircle size={10} /> {perf.pass_count || 0}</span>
                              </OverlayTrigger>
                              <OverlayTrigger overlay={<Tooltip>Failed: {perf.fail_count || 0}</Tooltip>}>
                                <span className="perf-badge danger"><FaTimesCircle size={10} /> {perf.fail_count || 0}</span>
                              </OverlayTrigger>
                            </div>
                          </td>
                          <td className="text-center">
                            <span className={`perf-badge ${perf.first_attempt_pass > 0 ? "success" : "neutral"}`}>
                              {perf.first_attempt_pass > 0 && <FaStar size={9} />} {perf.first_attempt_pass || 0}
                            </span>
                          </td>
                          <td className="text-center"><span className="perf-badge info">{perf.second_attempt_pass || 0}</span></td>
                          <td className="text-center"><span className="perf-badge warn">{perf.third_attempt_pass || 0}</span></td>
                          <td className="text-center"><span className="perf-badge danger">{perf.fourth_attempt_pass || 0}</span></td>
                          <td className="text-center">
                            <span style={{ fontSize: 12, fontWeight: 600, color }}>
                              {perf.pass_rate >= 70 ? <FaThumbsUp size={11} /> : <FaThumbsDown size={11} />} {text}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </div>
  );
};

export default CoursePerformance;
